import { useMemo } from "react";

import type { CurrencyCode, Locale } from "@ticketnjoy/types";

import { useLocale } from "@/lib/i18n";

/** Arapçada Latin rakam: çeviri metinlerine giren sayılarla aynı rakam sistemi kalsın. */
const INTL_LOCALE: Record<Locale, string> = {
  tr: "tr-TR",
  en: "en-US",
  ar: "ar-u-nu-latn",
};

export const ISTANBUL_TZ = "Europe/Istanbul";

type DateInput = string | number | Date;

const toDate = (value: DateInput) => (value instanceof Date ? value : new Date(value));

/**
 * Aktif dile bağlı biçimlendiriciler. Saat dilimi varsayılan olarak verilmez:
 * etkinlik formundaki `datetime-local` alanları tarayıcı saatiyle çalışıyor,
 * gösterim de aynı saatte kalmalı. Backend'in İstanbul gününe göre grupladığı
 * rapor verisinde `timeZone: ISTANBUL_TZ` açıkça geçilir.
 */
export function createFormat(locale: Locale) {
  const tag = INTL_LOCALE[locale];
  const numberFmt = new Intl.NumberFormat(tag);
  const decimalSeparator =
    numberFmt.formatToParts(1.5).find((p) => p.type === "decimal")?.value ?? ".";
  return {
    locale,
    tag,
    decimalSeparator,
    money: (amount: number, currency: CurrencyCode | string) =>
      new Intl.NumberFormat(tag, {
        style: "currency",
        currency,
        minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
        maximumFractionDigits: 2,
      }).format(amount),
    number: (value: number, options?: Intl.NumberFormatOptions) =>
      options ? new Intl.NumberFormat(tag, options).format(value) : numberFmt.format(value),
    /** `value` yüzde cinsinden (12.5 → "%12,5"). */
    percent: (value: number, fractionDigits = 0) =>
      new Intl.NumberFormat(tag, {
        style: "percent",
        maximumFractionDigits: fractionDigits,
      }).format(value / 100),
    date: (
      value: DateInput,
      options: Intl.DateTimeFormatOptions = { day: "2-digit", month: "short", year: "numeric" },
    ) => new Intl.DateTimeFormat(tag, options).format(toDate(value)),
    dateTime: (
      value: DateInput,
      options: Intl.DateTimeFormatOptions = {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      },
    ) => new Intl.DateTimeFormat(tag, options).format(toDate(value)),
    time: (
      value: DateInput,
      options: Intl.DateTimeFormatOptions = { hour: "2-digit", minute: "2-digit" },
    ) => new Intl.DateTimeFormat(tag, options).format(toDate(value)),
  };
}

export type Format = ReturnType<typeof createFormat>;

export function useFormat(): Format {
  const locale = useLocale();
  return useMemo(() => createFormat(locale), [locale]);
}
